/**
 * Environnement sélectionné de l'onglet : SOURCE UNIQUE pour les écrans
 * (`useEnvironment`) et pour le client HTTP commun (branché par
 * `bindEnvironmentSource`). Le localStorage ne fournit que la valeur
 * initiale : un autre onglet qui change d'environnement ne touche pas
 * celui-ci (ré-audit console N-1).
 */
import React, {
  createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppEnvironment,
  DEFAULT_ENVIRONMENT,
  readStoredEnvironment,
  storeSelectedEnvironment,
} from '../config/environment';
import { bindEnvironmentSource } from '../lib/http';

interface EnvironmentContextValue {
  environment: AppEnvironment;
  setEnvironment: (env: AppEnvironment) => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue>({
  environment: DEFAULT_ENVIRONMENT,
  setEnvironment: () => {
    throw new Error('[EnvironmentContext] setEnvironment appelé hors de EnvironmentProvider');
  },
});

/**
 * Fournit l'environnement sélectionné aux écrans et au client HTTP.
 *
 * Changer d'environnement VIDE le cache des requêtes : aucune donnée de
 * l'ancien environnement ne doit rester affichée pendant qu'une mutation
 * partirait vers le nouveau (audit console S-1). Les écrans montés
 * rechargent avec des clés qui portent le nouvel environnement.
 *
 * Le branchement sur le client HTTP se fait en `useLayoutEffect` : il
 * précède les `useEffect` des écrans enfants, donc leurs premières requêtes.
 */
export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [environment, setEnvironmentState] = useState<AppEnvironment>(readStoredEnvironment);
  const environmentRef = useRef(environment);

  useLayoutEffect(() => bindEnvironmentSource(() => environmentRef.current), []);

  const setEnvironment = useCallback((env: AppEnvironment) => {
    environmentRef.current = env;
    storeSelectedEnvironment(env);
    void queryClient.cancelQueries();
    queryClient.removeQueries();
    setEnvironmentState(env);
  }, [queryClient]);

  const value = useMemo(() => ({ environment, setEnvironment }), [environment, setEnvironment]);

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

/** Environnement sélectionné et son setter (le sélecteur de l'en-tête). */
export const useEnvironment = () => useContext(EnvironmentContext);
